import type { Citation } from './types.js';

/**
 * Severity of a root cause. Separate from `Rule.severity` because a root
 * cause is a *pattern* (often synthesised from several symptoms), and its
 * urgency is judged holistically.
 *
 *  - `tip`        — stylistic, efficiency-oriented.
 *  - `warning`    — likely to cap improvement if left unaddressed.
 *  - `injury-risk`— raises musculoskeletal load; flagged conservatively per
 *                   Kovacs/Ellenbecker and Kibler guidelines.
 */
export type RootCauseSeverity = 'tip' | 'warning' | 'injury-risk';

/**
 * A biomechanical / tactical root cause — the "upstream" fault that tends
 * to emit several symptom-level rule hits. The causal graph engine scores
 * each node by summing `weight` of fired rules that list the node in their
 * `causes`.
 */
export interface RootCause {
  readonly id: string;
  readonly labelPt: string;
  /**
   * 1–2 sentences (pt-BR) explaining WHY this matters, grounded in cited
   * literature. Written to be readable by a layperson.
   */
  readonly summaryPt: string;
  readonly severity: RootCauseSeverity;
  /**
   * Rule IDs this cause typically produces. Not exhaustive — the live
   * linkage is driven by `Rule.causes` in the rulebook.
   */
  readonly commonDownstreamSymptoms: readonly string[];
  readonly source: Citation;
}

/*
 * -------------------------------------------------------------------------
 * Citations (shared pool, kept lean — same authors as the rulebook)
 * -------------------------------------------------------------------------
 */

const ELLIOTT_REID_CRESPO: Citation = Object.freeze({
  author: 'Elliott, B., Reid, M., & Crespo, M.',
  year: 2009,
  title: 'Biomechanics of Advanced Tennis (ITF Publishing)',
});

const KOVACS_ELLENBECKER: Citation = Object.freeze({
  author: 'Kovacs, M. & Ellenbecker, T.',
  year: 2011,
  title: 'An 8-stage model for evaluating the tennis serve (Sports Health)',
});

const ROETERT_KOVACS: Citation = Object.freeze({
  author: 'Roetert, E.P. & Kovacs, M.',
  year: 2011,
  title: 'Tennis Anatomy (Human Kinetics)',
});

const LANDLINGER: Citation = Object.freeze({
  author: 'Landlinger, J., Lindinger, S. et al.',
  year: 2010,
  title: 'Key factors and timing patterns in the tennis forehand (J Sports Sci Med)',
});

const BAHAMONDE_KNUDSON: Citation = Object.freeze({
  author: 'Bahamonde, R. & Knudson, D.',
  year: 2003,
  title: 'Kinetics of the upper extremity — tennis forehand (J Sci Med Sport)',
});

const FLEISIG: Citation = Object.freeze({
  author: 'Fleisig, G., Nicholls, R., Elliott, B., & Escamilla, R.',
  year: 2003,
  title: 'Kinematics of world-class tennis serves (Sports Biomech)',
});

const ITF_CSSR: Citation = Object.freeze({
  author: 'Crespo, M. et al.',
  year: 2016,
  title: 'ITF Coaching & Sport Science Review (various issues)',
});

const KIBLER_CHANDLER: Citation = Object.freeze({
  author: 'Kibler, W.B. & Chandler, T.J.',
  year: 2003,
  title: 'Range of motion in junior tennis players (Sports Med)',
});

const MARTIN_ENERGY_FLOW: Citation = Object.freeze({
  author: 'Martin, C., Bideau, B., Kulpa, R. et al.',
  year: 2014,
  title: 'Energy flow analysis during the tennis serve (Med Sci Sports Exerc)',
});

const LOEHR_MENTAL: Citation = Object.freeze({
  author: 'Loehr, J.',
  year: 1990,
  title: 'The Mental Game: Winning at Pressure Tennis',
});

/*
 * -------------------------------------------------------------------------
 * Root cause nodes
 * -------------------------------------------------------------------------
 * ~20 entries covering kinetic chain, timing, grip/contact, movement,
 * mental / session-level. IDs are stable — rules reference them by string.
 */

function rc(partial: RootCause): RootCause {
  return Object.freeze(partial);
}

export const rootCauses: readonly RootCause[] = Object.freeze([
  rc({
    id: 'poor-trunk-rotation',
    labelPt: 'Rotação de tronco insuficiente',
    summaryPt:
      'O tronco é o motor do golpe: sem coiling (enrolamento) dos ombros em relação ao quadril, o braço compensa sozinho e a bola sai curta. A separação quadril–ombro alimenta a velocidade da raquete via cadeia cinética.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.kinetic.no-hip-rotation',
      'forehand.shoulder.no-turn',
      'backhand.shoulder.insufficient-turn',
      'serve.chain.hip-shoulder-separation',
      'serve.rotation.no-trunk-rotation',
      'overhead.shoulder.no-turn',
    ],
    source: ELLIOTT_REID_CRESPO,
  }),
  rc({
    id: 'late-preparation',
    labelPt: 'Preparação atrasada',
    summaryPt:
      'Quando a unit turn (giro inicial ombro–quadril junto) acontece depois da bola quicar, o golpe fica apressado e o ponto de contato atrasa — perda de potência e direção. Pros iniciam a preparação antes do quique.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.contact.late-contact',
      'forehand.tempo.rushed-swing',
      'movement.turn.unit-turn-late',
      'return.elbow.late-prep',
      'return.contact.late',
      'backhand.takeback.too-high',
    ],
    source: LANDLINGER,
  }),
  rc({
    id: 'arm-only-swing',
    labelPt: 'Golpe só de braço',
    summaryPt:
      'Sem contribuição de pernas e tronco, apenas o braço gera velocidade — resulta em pouca potência e alta carga no cotovelo/ombro. A energia deve subir pela cadeia cinética dos pés ao punho.',
    severity: 'injury-risk',
    commonDownstreamSymptoms: [
      'forehand.power.low-wrist-velocity',
      'forehand.kinetic.no-hip-rotation',
      'forehand.shoulder.no-turn',
      'backhand.kinetic.legs-quiet',
      'serve.stance.no-leg-drive',
      'serve.power.low-wrist-velocity',
    ],
    source: MARTIN_ENERGY_FLOW,
  }),
  rc({
    id: 'weak-leg-drive',
    labelPt: 'Impulso de pernas fraco',
    summaryPt:
      'A flexão e o empurrão das pernas somam até 20% da velocidade da raquete no saque e alimentam o peso transferido na base. Sem eles, o golpe fica "plano" e o ombro paga a conta.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'serve.stance.no-leg-drive',
      'serve.stance.insufficient-knee-bend',
      'volley.lowvolley.no-knee-bend',
      'forehand.height.low-ball-no-bend',
      'backhand.kinetic.legs-quiet',
      'movement.loadunload.weight-transfer',
    ],
    source: KOVACS_ELLENBECKER,
  }),
  rc({
    id: 'no-pronation',
    labelPt: 'Ausência de pronação no saque',
    summaryPt:
      'A pronação do antebraço (giro do punho para dentro no impacto) é a principal fonte de velocidade da raquete em saques de elite. Sem ela, o saque fica tipo "frying pan" e a rotação interna do ombro carrega toda a demanda.',
    severity: 'injury-risk',
    commonDownstreamSymptoms: [
      'serve.pronation.missing',
      'serve.grip.frying-pan',
      'serve.power.low-wrist-velocity',
      'serve.spin.flat-second',
    ],
    source: FLEISIG,
  }),
  rc({
    id: 'grip-too-continental-for-fh',
    labelPt: 'Empunhadura muito continental para forehand',
    summaryPt:
      'A continental no forehand achata a cara da raquete e dificulta o topspin moderno. Eastern/Semi-Western permitem elevar a bola com segurança acima da rede.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.topspin.low-racket-drop',
      'forehand.wrist.breaking-down',
      'forehand.height.high-ball-swing-volley-option',
    ],
    source: ROETERT_KOVACS,
  }),
  rc({
    id: 'western-grip-on-low-balls',
    labelPt: 'Empunhadura muito fechada para bolas baixas',
    summaryPt:
      'Western funciona em bolas altas mas exige muita flexão de joelhos e ataque de baixo para cima em bolas baixas. Falhar nisso gera contatos altos demais e perda de controle.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.grip.western-on-low-ball',
      'forehand.height.low-ball-no-bend',
    ],
    source: ROETERT_KOVACS,
  }),
  rc({
    id: 'late-contact-point',
    labelPt: 'Ponto de contato atrasado',
    summaryPt:
      'Bater a bola atrás do quadril reduz potência e biomechanically obriga o ombro a "puxar" em vez de empurrar — comum em preparação tardia ou footwork estático. Ideal: à frente do quadril dianteiro.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.contact.late-contact',
      'backhand.contact.behind-front-hip',
      'return.contact.late',
      'forehand.balance.falling-back',
    ],
    source: LANDLINGER,
  }),
  rc({
    id: 'no-unit-turn',
    labelPt: 'Falta de unit turn (giro inicial)',
    summaryPt:
      'O unit turn — girar ombros + quadris + raquete juntos assim que a bola sai da raquete do adversário — prepara o corpo antes da corrida. Sem isso, o jogador "persegue" a bola sem carregar potência.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.shoulder.no-turn',
      'backhand.shoulder.insufficient-turn',
      'movement.turn.unit-turn-late',
      'return.elbow.late-prep',
    ],
    source: ELLIOTT_REID_CRESPO,
  }),
  rc({
    id: 'insufficient-knee-flexion',
    labelPt: 'Flexão de joelhos insuficiente',
    summaryPt:
      'Joelhos pouco flexionados impedem carregar peso na perna de trás e liberar via drive vertical. Afeta saque, bolas baixas e volêis baixos especialmente.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'serve.stance.insufficient-knee-bend',
      'volley.lowvolley.no-knee-bend',
      'forehand.height.low-ball-no-bend',
      'serve.stance.no-leg-drive',
    ],
    source: KOVACS_ELLENBECKER,
  }),
  rc({
    id: 'eye-lift-at-contact',
    labelPt: 'Cabeça sobe no contato',
    summaryPt:
      'Levantar a cabeça ou soltar os olhos antes do impacto tira o ponto de referência que estabiliza o tronco — o erro comum é a bola espirrar para cima ou para a rede. "Head still, eyes down."',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.head.eyes-off-ball',
      'backhand.head.spinout',
      'return.head.still',
    ],
    source: ELLIOTT_REID_CRESPO,
  }),
  rc({
    id: 'locked-arm-contact',
    labelPt: 'Braço travado / hiperestendido no contato',
    summaryPt:
      'Contato com cotovelo totalmente travado amortece mal o impacto e sobrecarrega a articulação (risco de "tennis elbow"). Deixar ~5° de flexão dissipa a carga.',
    severity: 'injury-risk',
    commonDownstreamSymptoms: [
      'serve.contact.elbow-locked',
      'forehand.contact.elbow-too-straight',
      'backhand.contact.elbow-too-straight',
      'volley.elbow.too-straight',
    ],
    source: KIBLER_CHANDLER,
  }),
  rc({
    id: 'cramped-contact',
    labelPt: 'Contato muito próximo do corpo (cotovelo fechado)',
    summaryPt:
      'Cotovelo muito flexionado no impacto denota falta de alcance ou footwork tardio. Potência cai e a bola sai "empurrada".',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.contact.elbow-too-bent',
      'backhand.contact.elbow-too-bent',
      'volley.elbow.too-bent',
      'serve.contact.elbow-too-bent',
      'backhand.contact.too-close-body',
    ],
    source: ELLIOTT_REID_CRESPO,
  }),
  rc({
    id: 'wristy-swing',
    labelPt: 'Punho solto em excesso',
    summaryPt:
      'O punho deve estar firme no impacto: soltar cedo demais tira precisão e sobrecarrega os tendões do antebraço. Acelere com braço/antebraço, punho estabiliza.',
    severity: 'injury-risk',
    commonDownstreamSymptoms: [
      'forehand.wrist.breaking-down',
      'backhand.wrist.too-loose',
      'volley.wrist.wristy',
    ],
    source: ROETERT_KOVACS,
  }),
  rc({
    id: 'truncated-followthrough',
    labelPt: 'Follow-through truncado',
    summaryPt:
      'Parar o braço logo após o contato reduz a velocidade transferida à bola e indica medo/decelerar antes da hora. A cadeia completa termina com a raquete do outro lado do corpo.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.followthrough.truncated',
      'serve.followthrough.abbreviated',
      'backhand.followthrough.across-body',
    ],
    source: ELLIOTT_REID_CRESPO,
  }),
  rc({
    id: 'no-splitstep',
    labelPt: 'Sem split-step antes do golpe do adversário',
    summaryPt:
      'O split-step (pulinho preparatório) carrega elasticidade nos tendões e zera a inércia antes do primeiro passo. Sem ele, o tempo de reação sobe ~100 ms.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.footwork.no-splitstep',
      'return.splitstep.absent',
      'volley.splitstep.timing',
      'movement.splitstep.consistent',
    ],
    source: LANDLINGER,
  }),
  rc({
    id: 'poor-recovery',
    labelPt: 'Má recuperação para centro',
    summaryPt:
      'Não voltar à posição neutra entre golpes amplia a área aberta e dá ângulo ao oponente. Cada golpe precisa terminar com primeiro passo em direção ao centro.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.recovery.no-recovery-step',
      'movement.recovery.off-center',
      'movement.recovery.first-step',
      'movement.court.deep-recovery',
    ],
    source: ITF_CSSR,
  }),
  rc({
    id: 'rushed-tempo',
    labelPt: 'Ritmo apressado',
    summaryPt:
      'Swings muito curtos comprimem a janela de aceleração e costumam vir da ansiedade ou de preparação tardia. Jogos sob pressão amplificam o padrão.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'forehand.tempo.rushed-swing',
      'serve.tempo.swing-too-short',
      'return.swing.too-long',
      'mental.breathing.shallow',
    ],
    source: LOEHR_MENTAL,
  }),
  rc({
    id: 'session-fatigue',
    labelPt: 'Fadiga ao longo da sessão',
    summaryPt:
      'Queda > 15% na velocidade angular do punho entre o primeiro e o último terço da sessão costuma refletir fadiga muscular ou cognitiva — aumenta risco de lesão e erros.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'backhand.consistency.fatigue-late-session',
      'mental.focus.fatigue-late-set',
      'session.consistency.velocity-low',
    ],
    source: ITF_CSSR,
  }),
  rc({
    id: 'mental-pressure-leak',
    labelPt: 'Vazamento mental sob pressão',
    summaryPt:
      'Padrões como respiração curta, auto-fala negativa e erros em sequência sinalizam perda de foco em pontos-chave. Rituais entre pontos recuperam o controle parassimpático.',
    severity: 'tip',
    commonDownstreamSymptoms: [
      'mental.breathing.shallow',
      'mental.selftalk.negative',
      'mental.momentum.two-consecutive-errors',
      'mental.ritual.between-points',
      'mental.bigpoints.patterns',
    ],
    source: LOEHR_MENTAL,
  }),
  rc({
    id: 'poor-weight-transfer',
    labelPt: 'Transferência de peso insuficiente',
    summaryPt:
      'Sem passo de carga e descarga (load-unload), o golpe depende só de rotação — e a bola sai sem profundidade. A massa corporal deve seguir a bola.',
    severity: 'warning',
    commonDownstreamSymptoms: [
      'forehand.balance.falling-back',
      'movement.loadunload.weight-transfer',
      'serve.stance.no-leg-drive',
      'volley.stance.not-stepping-in',
    ],
    source: BAHAMONDE_KNUDSON,
  }),
]);

/** Fast lookup by ID. */
export const rootCauseById: Readonly<Record<string, RootCause>> = Object.freeze(
  rootCauses.reduce<Record<string, RootCause>>((acc, rc_) => {
    acc[rc_.id] = rc_;
    return acc;
  }, {}),
);
